import React from "react";
import {Button, List, Popconfirm, Row, Col, Input, Popover, Tooltip} from "antd";
import Helmet from "react-helmet";
import moment from "moment";
import { API } from 'aws-amplify';
import downloadjs from 'downloadjs';
import { Parser } from 'json2csv';
import qs from 'querystring';
import map from "lodash/map";
import get from 'lodash/get';
import capitalize from "lodash/capitalize";
import { connect } from "react-redux";
import { Switch, Route, Link } from "react-router-dom";
import { S3Image } from "aws-amplify-react";
import InfiniteScroll from "react-infinite-scroller";
import Loading from "components/loading";
import { mappingActions } from "redux/modules/mappings";
import MappingCreate from "../mapping-create";
import * as Styled from "./styled";

const truncate = (text, ln = 25) => text.substr(0, ln) + "...";
const productOptions = (options = []) =>
  options.map(i => `${i.name}: ${i.values[0]}`).join(", ");
const platform = id => "[" + capitalize(id.split(":")[0]) + "]";

class Mapping extends React.Component {
  constructor(props) {
    super(props);
    const query = qs.parse(props.location.search.substr(1));
    this.state = {
      searching: false,
      adding: false,
      downloading: false,
      editing: null,
      userId: query.userId
    };
  }

  componentWillMount() {
    this.props.dispatch(mappingActions.resetMappings());
  }

  onDelete = id => {
    this.props.dispatch(mappingActions.deleteMapping(id));
  };

  handleInfiniteOnLoad = () => {
    this.props.dispatch(mappingActions.loadMappings(this.props.mappings.next, 10, this.state.userId));
  };

  onSearch = query => {
    if (query) {
      this.setState({ searching: true });
      this.props.dispatch(mappingActions.searchMappings(query, true, this.state.userId));
    } else {
      this.setState({ searching: false });
    }
  };

  onDownload = () => {
    this.setState({ downloading: true });
    API.get("api", "mappings", { queryStringParameters: { limit: 0 }}).then(
      ({ results }) => {
        const json2csvParser = new Parser({
          fields: [
            { label: 'Mapping name', value: 'name' },
            { label: 'SKU', value: 'sku' },
            { label: 'Catalog', value: 'catalogName' },
            { label: 'Size', value: 'product_size' },
            { label: 'Images count', value: (row) => row.images.length },
            { label: 'Orders count', value: 'orders' },
            { label: 'Date Created', value: (row) => new Date(row.createdAt).toLocaleDateString() },
            { label: 'Date Updated', value: (row) => new Date(row.updatedAt).toLocaleDateString() },
            { label: 'Files', value: (row) => row.images.map(i => i.split('/')[1]).join(', ') },
          ]
        });
        const csv = json2csvParser.parse(results);
        downloadjs(csv, 'mappings.csv', 'text/csv');
        this.setState({ downloading: false });
      },
      (e) => {
        this.setState({ downloading: false });
        alert(e.message);
      }
    )
  };

  onQueryChange = e => {
    this.setState(
      {
        query: e.target.value
      },
      () => {
        if (!this.state.query) {
          this.onSearch();
        }
      }
    );
  };

  renderPopover = (item) => {
    return (
      <div style={{ maxWidth: 600, maxHeight: 600, minWidth: 200, minHeight: 200 }}>
        <S3Image level="public" imgKey={`resized/${get(item, 'images.0')}`}  />
      </div>
    );
  };

  renderItem = (item, idx) => {
    const { userId = '' } = this.state;
    return (
      <List.Item
        actions={[
          <Link to={`/mappings/${item.id}?userId=${userId}`}>
            <Button ghost title="Edit Mapping" type="primary">
              Edit
            </Button>
          </Link>,
          <Popconfirm
            title="Are you sure you want to delete this mapping?"
            onConfirm={() => this.onDelete(item.id)}
          >
            <Button type="primary" icon="delete" title="Delete Mapping" ghost />
          </Popconfirm>
        ]}

      >
        <List.Item.Meta
          avatar={
            <Popover
              trigger={["hover"]}
              content={this.renderPopover(item)}
              placement="top"
            >
              <div>
                <S3Image level="public" imgKey={`resized/${get(item, 'images.0')}`} />
              </div>
            </Popover>
          }
          title={<Link to={`/mappings/${item.id}?userId=${userId}`}>{idx+1}) {item.name}</Link>}
          description={
            <div>
              <div>
                <span>
                  <strong>Mapping:</strong>{" "}
                  <span>
                    {get(item, 'images.length')} files, {item.catalogName} -{" "}
                    {item.product_size} {item.quantity} each
                  </span>
                </span>
                <span> | </span>
                <strong>SKU:</strong> <span>{item.sku}</span>
                <span> | </span>
                {item.customized && <span>Multiple choice item | </span>}
                <strong>Created: </strong>
                <span>{moment(item.createdAt).fromNow()}</span>
                 {" "}<strong>Last Updated: </strong>
                <span>{moment(item.updatedAt).fromNow()}</span>
              </div>
              <div>
                <strong>Linked to: </strong>
                {map(
                  item.products,
                  p =>
                    `${platform(p.id)} ${truncate(p.title)} ${productOptions(
                      p.productOptions
                    )}`
                ).join(" | ")}
              </div>
            </div>
          }
        />
      </List.Item>
    );
  };

  render() {
    const {
      data,
      hasMore,
      searchResults,
      hasMoreResults,
      loading,
      searching
    } = this.props.mappings;

    return (
      <Styled.Container>
        <Helmet title="Mappings" />
        <h1>Mappings</h1>
        <p>
          Mappings are the things that define a particular fulfillable
          product/variation. They consist of the images used to print the
          product combined with the Catalog and Print size they need to be
          printed as. The Mappings exist separate from your store's product so
          that the same Mapping can be used on different selling platforms. You
          will need to link a Mapping to a specific product/variaition to allow
          orders for that product to be fulfilled.
        </p>
        <p>
          Please note that it is easiest to create a new Mapping from a New
          Order or a Store Listing as you then will have a product/variation
          name that can be used to populate the name of the Mapping.
        </p>
        <Row className="search">
          <Col span={12}>
            <Row gutter={8}>
              <Col span={20}>
                <Input.Search
                  value={this.state.query}
                  onChange={this.onQueryChange}
                  allowClear
                  enterButton
                  size="large"
                  onSearch={this.onSearch}
                  placeholder="Search mappings"
                />
              </Col>
              <Col span={2}>
                <Tooltip title="Download a CSV file of all your mappings">
                  <Button loading={this.state.downloading} size="large" icon="download" onClick={this.onDownload} />
                </Tooltip>
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <div className="text-right">
             <Link to="/bulk-orders/create">
                <Button type="default" title="Create bulk order from existing Mappings" size="large">
                  Create an Order
                </Button>
              </Link>{" "}
               <Link to="/bulk-mapping">
                <Button type="default" title="Create multiple Mappings" size="large">
                  Bulk Mapping
                </Button>
              </Link>{" "}
              <Link to="/mappings/new">
                <Button
                  type="primary"
                  title="Create a new Mapping"
                  size="large"
                >
                  Create Mapping
                </Button>
              </Link>
            </div>
          </Col>
        </Row>
        {this.state.searching ? (
          <List
            loading={searching}
            renderItem={this.renderItem}
            dataSource={searchResults}
          >
            {searching && <Loading size="small" />}
            {!searching && hasMoreResults && <div>Only showing the top 10 results - try a more specific search</div>}
          </List>
        ) : (
          <InfiniteScroll
            initialLoad={true}
            pageStart={0}
            loadMore={this.handleInfiniteOnLoad}
            hasMore={!loading && hasMore}
            useWindow={true}
          >
            <List renderItem={this.renderItem} dataSource={data}>
              {loading && hasMore && <Loading size="small" />}
            </List>
          </InfiniteScroll>
        )}
        <Switch>
          <Route exact path="/mappings/new" component={MappingCreate} />
          <Route exact path="/mappings/:id" component={MappingCreate} />
        </Switch>
      </Styled.Container>
    );
  }
}

export default connect(state => ({
  session: state.session,
  mappings: state.mappings
}))(Mapping);
